import { Directive, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroupDirective } from '@angular/forms';
import { LoggingService } from '@com-delta-idp/mnx-utils';
import { Store } from '@ngrx/store';
import { ReplaySubject, Subject, Subscription, combineLatest } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  startWith,
  take,
  takeUntil
} from 'rxjs/operators';
import { UPDATE_FORM } from '../../model/forms.model';

@Directive({
  selector: '[ngc-form-store]',
  exportAs: 'ngcFormStore'
})
export class FormStoreDirective implements OnInit, OnDestroy {
  // tslint:disable-next-line:no-input-rename
  @Input('ngc-form-store') path: string;
  syncFromStoreComplete: ReplaySubject<any> = new ReplaySubject<any>(1);
  formSubscriptions: Subscription;
  formValueRef = {};
  private unsubscribe: Subject<void> = new Subject();

  constructor(
    private formGroupDirective: FormGroupDirective,
    private loggingService: LoggingService,
    private store: Store<any>
  ) {}

  ngOnInit() {
    this.applyValuesFromStore();
  }

  getStoredValueFor(controlName, defaultValue = '') {
    if (this.formValueRef && this.formValueRef[controlName]) {
      return this.formValueRef[controlName];
    } else {
      return defaultValue;
    }
  }

  private applyValuesFromStore() {
    this.store
      .select((state) => state.formReducer[this.path])
      .pipe(take(1))
      .subscribe(
        (formValue) => {
          if (formValue) {
            this.formValueRef = formValue;
            this.syncFromStoreComplete.next(formValue);
            this.formGroupDirective.form.patchValue(formValue);
          }
        },
        (err) => {
          this.loggingService.log('Caught error: ' + err);
        }
      );
    this.formSubscriptions = combineLatest(
      [this.formGroupDirective.form.valueChanges].map((obs) =>
        obs.pipe(startWith(null as string))
      )
    )
      .pipe(
        debounceTime(100),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev[0]) === JSON.stringify(curr[0])
        ),
        takeUntil(this.unsubscribe)
      )
      .subscribe(
        (value) => {
          this.updatedFullState(value[0]);
          this.store.dispatch({
            type: UPDATE_FORM,
            payload: {
              value: this.formValueRef,
              path: this.path
            }
          });
        },
        (err) => {
          this.loggingService.log('Caught error: ' + err);
        }
      );
  }

  private updatedFullState(value) {
    if (Object.isFrozen(this.formValueRef)) {
      this.formValueRef = { ...this.formValueRef };
    }
    for (const controlName of Object.keys(value)) {
      Object.defineProperty(this.formValueRef, controlName, {
        writable: true,
        configurable: true
      });
      const isAssignable = Object.getOwnPropertyDescriptor(
        this.formValueRef,
        controlName
      );
      if (isAssignable && isAssignable.configurable) {
        this.formValueRef[controlName] = value[controlName]; // trackJS err triggered
      }
    }
    return this.formValueRef;
  }

  private unsubscribeFormControls() {
    if (this.formSubscriptions) {
      this.formSubscriptions.unsubscribe();
    }
  }

  ngOnDestroy() {
    this.unsubscribeFormControls();
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.unsubscribe.unsubscribe();
  }
}
